import { useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { FormFieldCombobox, FormFieldDropdown } from '~/components/FormField';
import { IconButton } from '~/components/IconButton';
import { isEnvEnabled } from '~/lib/env';
import { useLabelList } from '~/modules/settings/api/queries/labelList';
import { RoleMap } from '~/modules/settings/constants/RoleMap';
import { DEFAULT_DROPDOWN_PAGE_SIZE } from '~settings/constants';
import { toDropdownList } from '~settings/utils/transformers';

interface ExternalPermissionsProps {
  isEditing?: boolean;
  name: string;
}

const EXTERNAL_USER_ROLES = [
  'LABEL_ADMIN',
  'VIDEO_COORDINATOR',
  'RELEASE_COORDINATOR',
] as const;

const mapRoles = (
  roles: Readonly<(keyof typeof RoleMap)[]>,
  t: (key: string) => string,
) =>
  roles.map((role) => ({
    value: role,
    label: t(RoleMap[role].name),
  }));

export function ExternalPermissions({
  isEditing,
  name,
}: ExternalPermissionsProps) {
  const { t: rolesT } = useTranslation('settings', { keyPrefix: 'roles' });
  const { t } = useTranslation('settings', { keyPrefix: 'user' });
  const dropdownPaginationEnabled = isEnvEnabled(
    'VITE_CONTAINER_SETTINGS_DROPDOWN_PAGINATION_ENABLED',
    false,
  );

  const [search, setSearch] = useState<string>();

  const { labelList } = useLabelList({
    skip: 0,
    take: DEFAULT_DROPDOWN_PAGE_SIZE,
    ...(isEditing ? { canEditUsers: true } : { canCreateUsers: true }),
  });

  const { fields, append, remove } = useFieldArray({
    name,
  });

  const canRemove = fields.length > 1;
  const canAddMore = fields.length < EXTERNAL_USER_ROLES.length;

  const labels = labelList?.data ?? [];

  return (
    <ul className="flex flex-col gap-md">
      {fields.map((item, index) => (
        <li key={item.id} className="grid grid-cols-3 items-start gap-md">
          <FormFieldDropdown
            name={`${name}.${index}.role`}
            label={t('fields.role')}
            placeholder={t('placeholders.role')}
            options={mapRoles(EXTERNAL_USER_ROLES, rolesT)}
          />
          <FormFieldCombobox
            name={`${name}.${index}.labels`}
            label={t('fields.labels')}
            placeholder={t('placeholders.labels')}
            options={toDropdownList(labels)}
            multiselect
            displaySelectedOptions
            search={search}
            onSearchChange={setSearch}
            clientSearch={!dropdownPaginationEnabled}
          />
          {canRemove ? (
            <IconButton
              iconType="trash"
              size="xlarge"
              className="self-end"
              onClick={() => remove(index)}
              aria-label={t('removeRole')}
            />
          ) : null}
        </li>
      ))}
      {canAddMore ? (
        <li className="flex items-center">
          <Button
            onClick={() => {
              append({ role: '', labelGroup: '', labels: [] });
            }}
            className="inline-flex items-center justify-center gap-md p-md no-underline [&_i]:text-body-lg"
            type="button"
            variant="tertiary"
          >
            <Icon type="plus" aria-hidden />
            {t('addRole')}
          </Button>
        </li>
      ) : null}
    </ul>
  );
}

ExternalPermissions.displayName = 'ExternalPermissions';
